import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import EditText from '../../components/ui/EditText';
import Switch from '../../components/ui/Switch';
import Input from '../../components/ui/Input';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as URLS from '../../../urls';
import Icon from 'components/AppIcon';
import axios from 'axios';
import { getMyCartService } from 'redux/services/general_service';
import { toast } from 'react-toastify';

const Login = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar'; // متغير للـ RTL

  const [isLoading, setIsLoading] = useState(false);
  const [showGuestOptions, setShowGuestOptions] = useState(false);

  const location = useLocation();

  const from = location.state?.from?.pathname || '/home-page';
  const hasCartItems = JSON.parse(localStorage.getItem('cartItems') || '[]').length > 0;

  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    account: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  // useEffect لتطبيق dir على html (عشان يأثر على كل الصفحة)
  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRtl, i18n.language]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = t('passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('passwordTooShort');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.account !== '' && formData.password !== '') {
      setLoading(true); // Start loading
      const token = null;
      axios
        .post(`${URLS.baseUrl}/${URLS?.loginURL}`, formData)
        .then((response) => {
          if (response?.status === 200) {
            //   token = response?.data?.data?.token;
            localStorage.setItem('Token', response?.data?.data?.token);
            localStorage.setItem(
              'permissions',
              JSON.stringify(response?.data?.data?.user?.permissions)
            );
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem(
              'user',
              JSON.stringify({
                id: response.data.data?.user?.id,
                first_name: response.data.data?.user?.first_name,
                last_name: response.data.data?.user?.last_name,
                email: response.data.data?.user?.email,
                phone: response.data.data?.user?.phone,
                address: response.data.data?.user?.address,
                province: response.data.data?.user?.province,
              })
            );
            //
            const localCart = JSON.parse(localStorage.getItem('cartItems') || '[]');
            if (localCart.length > 0) {
              try {
                for (const item of localCart) {
                  console.log(item);
                  axios.post(
                    `${URLS.baseUrl}/${URLS?.cartURL}`,
                    {
                      product_id: item.id,
                      quantity: item.quantity,
                      product_variant_id: item.selectedVariant?.id,
                    },
                    {
                      headers: {
                        Accept: '*/*',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${response?.data?.data?.token}`, // Replace with your actual token
                      },
                    }
                  );
                }
                // مسح السلة المحلية بعد الدمج
                localStorage.removeItem('cartItems');
                localStorage.removeItem('cartCount');
              } catch (error) {
                console.error('Error merging carts:', error);
              }
            }
            navigate('/');
            setTimeout(() => {
              dispatch(getMyCartService());
            }, 300);
          } else {
            response?.payload?.errors?.map((error) => toast.warn(error));
          }
        })
        .catch((error) => {
          const validationErrors = error.response.data.errors;
          if (validationErrors) {
            Object.keys(validationErrors).forEach((key) => {
              validationErrors[key].forEach((message) => {
                toast(t(message));
              });
            });
          }
        })
        .finally(() => {
          /* navigate("/");
          window.location.reload();*/

          setLoading(false);
        });
    } else {
      setError(t.invalidCredentials);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated === 'true') {
      navigate(from, { replace: true });
    }

    setShowGuestOptions(hasCartItems);
  }, [navigate, from, hasCartItems]);

  return (
    <>
      <Helmet>
        <title>Welcome Back - Sign In | Star E-commerce Platform</title>
        <meta
          name="description"
          content="Sign in to your Star account to access your orders, wishlist and personalized shopping experience. Secure login with remember me option for easy access."
        />
        <meta property="og:title" content="Welcome Back - Sign In | Star E-commerce Platform" />
        <meta
          property="og:description"
          content="Sign in to your Star account to access your orders, wishlist and personalized shopping experience. Secure login with remember me option for easy access."
        />
      </Helmet>

      <main
        dir={isRtl ? 'rtl' : 'ltr'} // هون الـ dir الرئيسي
        className={`w-full min-h-screen ${isRtl ? 'text-right' : 'text-left'}`} // text-right عام للعربي
      >
        <div
          className={`flex flex-row ${
            isRtl ? 'flex-row-reverse justify-end' : 'justify-start'
          } items-center w-full min-h-screen bg-white`}
        >
          {/* Left Image Section - في RTL هيتصير يمين، hidden on mobile */}
          <div
            className={`hidden lg:flex flex-col justify-start items-start w-full lg:w-[58%] xl:w-[60%] ${
              isRtl ? 'lg:pl-[0px]' : ''
            }`}
          >
            <img
              src="/images/img_black_friday_co.png"
              alt="Black Friday Shopping Cart with Colorful Bags"
              className="w-full h-screen object-cover"
            />
          </div>

          {/* Right Login Form Section - في RTL هيتصير يسار */}
          <div
            className={`flex flex-col justify-start items-start w-full lg:w-[42%] xl:w-[40%] px-4 sm:px-8 ${
              isRtl
                ? 'lg:pl-[68px] lg:pr-[56px]' // padding يسار كبير، يمين صغير في RTL
                : 'lg:px-[68px] lg:pr-[56px]'
            } py-8 lg:py-0`}
          >
            {/* Logo Section - في RTL: justify-end و mr بدل ml */}
            <div
              className={`w-full flex ${
                isRtl
                  ? 'justify-center lg:mr-[0px]'
                  : 'justify-center lg:justify-center lg:ml-[0px]'
              } mb-8 lg:mb-8`}
            >
              <img
                src="/images/img_black_and_yellow.png"
                alt="Star Logo"
                className="w-[150px] sm:w-[240px] lg:w-[268px] h-auto"
              />
            </div>

            {/* Form Container */}
            <div className="flex flex-col justify-center items-center w-full max-w-md mx-auto lg:max-w-none lg:w-[80%]">
              {/* Welcome Text - text-right في RTL */}
              <div
                className={`w-full text-center ${
                  isRtl ? 'lg:text-center' : 'lg:text-center'
                } mb-6 lg:mb-8`}
              >
                <h1
                  className={`text-2xl sm:text-3xl lg:text-[28px] font-bold leading-tight lg:leading-[35px] text-[#2c2c2c] mb-2 lg:mb-[6px] font-['Gilroy-Bold'] ${
                    isRtl ? 'text-center' : 'text-center'
                  }`}
                >
                  {t('welcomeBack')}
                </h1>
                <p
                  className={`text-sm lg:text-[14px] font-medium leading-[17px] text-[#6c6c6c] font-['Gilroy-Medium'] ${
                    isRtl ? 'text-center' : 'text-center'
                  }`}
                >
                  {t('continueShopping')}
                </p>
              </div>

              {/* Form Fields */}
              <div className="flex flex-col gap-6 lg:gap-[14px] w-full mb-6 lg:mb-8">
                <div className="flex flex-col gap-4 lg:gap-[14px] w-full">
                  {/* Email Input */}
                  <Input
                    label={t('email_phone')}
                    type="text"
                    name="account"
                    placeholder={t('emailPhonePlaceholder')}
                    value={formData.account}
                    onChange={handleInputChange}
                    error={errors.account}
                    required
                    disabled={loading}
                    className={isRtl ? 'text-right' : 'text-left'} // نص يمين في RTL
                  />

                  {/* Password Input Container */}
                  <div className="relative">
                    <Input
                      label={t('passwordLabel')}
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder={t('passwordPlaceholder')}
                      value={formData.password}
                      onChange={handleInputChange}
                      error={errors.password}
                      required
                      disabled={loading}
                      className={isRtl ? 'text-right' : 'text-left'} // نفس الشيء
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute ${
                        isRtl ? 'left-3' : 'right-3' // يسار للعربي
                      } top-9 text-text-secondary hover:text-text-primary transition-smooth`}
                      disabled={loading}
                    >
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                    </button>
                  </div>
                </div>

                {/* Remember Me and Forgot Password Row - flex-row-reverse في RTL */}
                <div
                  className={`flex flex-col sm:flex-row ${
                    isRtl
                      ? 'sm:flex-row-reverse justify-end sm:justify-between' // عكس الترتيب
                      : 'justify-between'
                  } items-start sm:items-center gap-4 sm:gap-0 w-full`}
                >
                  {/*  <div
                    className={`flex flex-row ${
                      isRtl
                        ? 'flex-row-reverse justify-end mr-2' // عكس و margin يمين
                        : 'justify-start ml-2'
                    } items-center gap-2`}
                  >
                    <Switch
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                      size="small"
                      layout_width="auto"
                      position="relative"
                      className="switch-component"
                      id="remember-me"
                      name="rememberMe"
                      aria-label="Remember me toggle"
                    />
                    <span
                      className={`text-xs lg:text-[12px] font-medium leading-[15px] text-[#6c6c6c] font-['Gilroy-Medium'] ${
                        isRtl ? 'mr-2' : 'ml-2' // margin يمين في RTL
                      }`}
                    >
                      {t('rememberMe')}
                    </span>
                  </div>*/}
                  <Link
                    to="/forgot-password"
                    className={`text-xs lg:text-[12px] font-medium leading-[15px] text-[#ca0d2d] font-['Gilroy-Medium'] hover:underline ${
                      isRtl ? 'text-right' : 'text-left'
                    }`}
                    variant="link"
                    size="small"
                    target="_self"
                    rel="noopener"
                    aria-label="Forgot password link"
                    onClick={() => {}}
                  >
                    {t('forgotPassword')}
                  </Link>
                </div>

                {/* Sign In Button */}
                <Button
                  text={loading ? t('loading') : t('signIn')}
                  text_font_size="16"
                  text_font_family="Gilroy-SemiBold"
                  text_color="#ffffff"
                  fill_background_color="#ca0d2d"
                  border_border_radius="8px"
                  layout_width="100%"
                  padding="12px 34px"
                  position="relative"
                  layout_gap="0"
                  margin="0"
                  variant="primary"
                  size="medium"
                  className={`pt-4 pb-4 signin-button ${isRtl ? 'text-right' : 'text-left'}`} // نص يمين في RTL
                  onClick={handleSubmit}
                />
              </div>

              {/* Divider Line */}
              <div className="w-full h-[1px] bg-[#d2d2d2] mb-2 lg:mb-2"></div>

              {/* Sign Up Link - mr-2 في RTL */}
              <div
                className={`flex flex-row ${
                  isRtl ? 'flex-row-reverse justify-center' : 'justify-center'
                } items-center gap-2 w-full`}
              >
                <span
                  className={`text-xs lg:text-[12px] font-medium leading-[15px] text-[#727272] font-['Gilroy-Medium'] ${
                    isRtl ? 'mr-2' : 'ml-2'
                  }`}
                >
                  {t('noAccount')}
                </span>
                <Link
                  to="/signup"
                  className={`text-xs lg:text-[12px] font-semibold leading-[18px] text-[#ca0d2d] font-['Poppins'] hover:underline ${
                    isRtl ? 'mr-2' : 'ml-2'
                  }`}
                  variant="link"
                  size="small"
                  target="_self"
                  rel="noopener"
                  aria-label="Get started signup link"
                  onClick={() => (window.location.href = '/register')}
                >
                  {t('createOne')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Login;
